import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getWhatsAppSnippets, createWhatsAppSnippet, updateWhatsAppSnippet, deleteWhatsAppSnippet, uploadWhatsAppMedia, getWhatsAppInteractivePresets, createWhatsAppInteractivePreset, updateWhatsAppInteractivePreset, deleteWhatsAppInteractivePreset, getTagSuggestions } from '../utils/api';
import {
  SNIPPET_PRESET_KEYS,
  formatSnippetCategoryLabel,
  isPresetSnippetCategory,
  normalizeSnippetCategoryForSave,
} from '../utils/whatsappSnippetCategories';
import {
  EMPTY_INTERACTIVE_PRESET_FORM,
  EMPTY_RULE,
  presetRecordToForm,
  formToStoredButtons,
  countListRows,
  ruleIsActive,
  MAX_LIST_ROWS,
  LIST_BUTTON_MAX,
  parseStoredButtons,
} from '../utils/interactivePresetFormat';
import { FaBold, FaItalic, FaStrikethrough, FaCode, FaLink, FaListUl, FaListOl, FaSmile, FaUpload, FaMicrophone, FaMapMarkerAlt, FaPlus, FaTimes, FaEdit, FaTrash, FaCopy, FaTools, FaSearch, FaLayerGroup, FaChevronRight, FaChevronLeft, FaChevronDown } from 'react-icons/fa';
import EmojiPicker from 'emoji-picker-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useToast } from './Toast';
import './WhatsApp.css';

/** Message-style previews (how it looks on a phone) — use system UI stack, not Real brand webfonts */
const whatsappFontFamily =
  'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Fix Leaflet marker icon
if (typeof L !== 'undefined' && L.Icon && L.Icon.Default) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}

const LocationMarker = ({ position, setPosition }) => {
  useMapEvents({ click(e) { setPosition([e.latlng.lat, e.latlng.lng]); } });
  return position ? <Marker position={position} /> : null;
};

/** Must be a stable top-level component — defining inside WhatsAppTools remounts on every parent render and collapses the panel on each keystroke. */
function InteractivePresetRuleEditor({ language, rule, onTagsChange, onPriorityChange, onActivityChange, suggestedTags = [] }) {
  const [open, setOpen] = useState(false);
  const hasRule = ruleIsActive(rule);
  const tags = rule?.add_tags || [];
  const [tagInput, setTagInput] = useState('');
  const addTag = () => {
    const t = tagInput.trim().toLowerCase().slice(0, 24);
    if (!t || tags.includes(t) || tags.length >= 5) return;
    onTagsChange([...tags, t]);
    setTagInput('');
  };
  const isAr = language === 'ar';
  return (
    <div style={{ marginTop: '6px' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          color: hasRule ? '#5b21b6' : '#334155',
          fontWeight: 700,
          padding: '4px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        ⚡ {open ? (isAr ? 'طيّ' : 'Collapse') : hasRule ? (isAr ? 'تعديل تلقائي' : 'Edit auto') : (isAr ? '+ تلقائي عند الرد' : '+ On client tap')}
      </button>
      {open && (
        <div style={{ background: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '12px', marginTop: '6px', display: 'grid', gap: '10px' }}>
          <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#1e1b4b', lineHeight: 1.45 }}>
            {isAr
              ? 'الوسوم والأولوية تُطبّقان تلقائياً عند ضغط العميل. التذكير أدناه للموظف فقط.'
              : 'Tags and priority apply automatically when the client taps. The reminder below is for staff only.'}
          </p>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e1b4b', marginBottom: '6px' }}>
              {isAr ? 'وسوم (تلقائي)' : 'Tags (automatic)'}
            </div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '6px' }}>
              {tags.map((t, i) => (
                <span key={i} style={{ padding: '3px 9px', background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '20px', fontSize: '12px', color: '#3b0764', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {t}
                  <button type="button" onClick={() => onTagsChange(tags.filter((_, ti) => ti !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5b21b6', padding: 0, fontSize: '14px', lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
            {tags.length < 5 && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  className="wa-input"
                  placeholder={isAr ? 'وسم' : 'Tag'}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value.toLowerCase().replace(/\s/g, '-').slice(0, 24))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  style={{ flex: 1, fontSize: '13px', padding: '6px 10px', color: '#0f172a' }}
                />
                <button type="button" onClick={addTag} style={{ padding: '6px 12px', background: '#7c3aed', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#fff', fontWeight: 700 }}>
                  +
                </button>
              </div>
            )}
            {suggestedTags.length > 0 && tags.length < 5 && (
              <div style={{ marginTop: '6px', display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>{isAr ? 'مستخدمة:' : 'Used:'}</span>
                {suggestedTags.filter((s) => !tags.includes(s.tag)).slice(0, 8).map((s) => (
                  <button
                    key={s.tag}
                    type="button"
                    onClick={() => { if (tags.length < 5) onTagsChange([...tags, s.tag]); }}
                    style={{ padding: '2px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '11px', color: '#334155', fontWeight: 600, cursor: 'pointer' }}
                  >
                    {s.tag}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e1b4b', marginBottom: '6px' }}>
              {isAr ? 'أولوية (تلقائي)' : 'Priority (automatic)'}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                ['', isAr ? '—' : '—'],
                ['low', isAr ? 'منخفض' : 'Low'],
                ['medium', isAr ? 'متوسط' : 'Med'],
                ['high', isAr ? 'عالٍ' : 'High'],
              ].map(([v, label]) => (
                <button
                  key={v || 'none'}
                  type="button"
                  onClick={() => onPriorityChange(v)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '8px',
                    border: (rule?.priority || '') === v ? '2px solid #5b21b6' : '1px solid #cbd5e1',
                    background: (rule?.priority || '') === v ? '#ede9fe' : '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: (rule?.priority || '') === v ? '#3b0764' : '#0f172a',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e1b4b', marginBottom: '6px' }}>
              {isAr ? 'تذكير جدولة (للموظف)' : 'Schedule reminder (staff)'}
            </div>
            <input
              className="wa-input"
              placeholder={isAr ? 'مثال: معاينة العقار' : 'e.g. Viewing'}
              value={rule?.suggest_activity || ''}
              onChange={(e) => onActivityChange(e.target.value.slice(0, 200))}
              style={{ width: '100%', fontSize: '13px', padding: '8px 10px', color: '#0f172a' }}
            />
            <p style={{ margin: '6px 0 0', fontSize: '11px', fontWeight: 500, color: '#475569' }}>
              {isAr ? 'اختياري — يظهر زر جدولة في صندوق الوارد.' : 'Optional — shows a Schedule button in the inbox.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_SNIPPET = { category: 'general', label: '', content: '', message_type: 'text', media_metadata: {} };

const WhatsAppTools = ({ language = 'ar', userRole = 'employee', userPermissions = null, initialSubTab = 'quickreplies', onTabChange }) => {
  const toast = useToast();
  const isManagerOrOwner = userRole === 'manager' || userRole === 'owner';
  // canManage: owner/manager always yes; employees need wa_tools_manage
  const canManage = isManagerOrOwner || (userPermissions ? !!userPermissions.wa_tools_manage : false);
  const [subTab, setSubTab] = useState(initialSubTab);

  // ── Quick Replies state ────────────────────────────────────────────────────
  const [snippets, setSnippets]               = useState([]);
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const [snippetsSaving, setSnippetsSaving]   = useState(false);
  const [editingSnippetId, setEditingSnippetId] = useState(null);
  const [snippetForm, setSnippetForm]         = useState({ ...EMPTY_SNIPPET });
  const [expandedSnippets, setExpandedSnippets] = useState(new Set());
  const [snippetListQuery, setSnippetListQuery] = useState('');
  const [snippetCategoryFilter, setSnippetCategoryFilter] = useState('all');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkModal, setShowLinkModal]     = useState(false);
  const [linkModalUrl, setLinkModalUrl]       = useState('');
  const [linkModalText, setLinkModalText]     = useState('');
  const [mediaUploading, setMediaUploading]   = useState({ image: false, video: false, audio: false });
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [smartUrlModal, setSmartUrlModal]     = useState(null);
  const snippetContentRef = useRef(null);

  // ── Interactive Presets state ──────────────────────────────────────────────
  const [presets, setPresets]               = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsSaving, setPresetsSaving]   = useState(false);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [presetForm, setPresetForm]         = useState({ ...EMPTY_INTERACTIVE_PRESET_FORM });
  const [orgTagSuggestions, setOrgTagSuggestions] = useState([]);

  // ── Load data on tab switch ────────────────────────────────────────────────
  useEffect(() => {
    if (subTab === 'quickreplies') loadSnippets();
    if (subTab === 'interactive')  { loadPresets(); loadOrgTagSuggestions(); }
    onTabChange?.(subTab);
  }, [subTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialSubTab === 'interactive') setSubTab('interactive');
    else setSubTab('quickreplies');
  }, [initialSubTab]);

  // ── Quick Replies helpers ──────────────────────────────────────────────────
  const loadSnippets = async () => {
    try {
      setSnippetsLoading(true);
      const res = await getWhatsAppSnippets();
      setSnippets(Array.isArray(res.data?.snippets) ? res.data.snippets : []);
    } catch { toast.error(language === 'ar' ? 'تعذر تحميل الردود الجاهزة' : 'Failed to load quick replies'); }
    finally { setSnippetsLoading(false); }
  };

  const detectTextDirection = (text) => {
    if (!text) return language === 'ar' ? 'rtl' : 'ltr';
    return /[\u0600-\u06FF]/.test(text) ? 'rtl' : 'ltr';
  };

  const renderFormattedText = (rawText) => {
    if (!rawText) return null;
    return String(rawText).split('\n').map((line, li) => (
      <span key={li}>
        {li > 0 && <br />}
        {line.split(/(https?:\/\/[^\s]+)/g).map((part, pi) => {
          if (/^https?:\/\//.test(part)) return <a key={pi} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>{part}</a>;
          const re = /\*([^*]+)\*|_([^_]+)_|~([^~]+)~|`([^`]+)`/g;
          const out = []; let cursor = 0; let m;
          while ((m = re.exec(part)) !== null) {
            if (m.index > cursor) out.push(<span key={cursor}>{part.slice(cursor, m.index)}</span>);
            if (m[1]) out.push(<strong key={m.index}>{m[1]}</strong>);
            else if (m[2]) out.push(<em key={m.index}>{m[2]}</em>);
            else if (m[3]) out.push(<s key={m.index}>{m[3]}</s>);
            else if (m[4]) out.push(<code key={m.index} style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.05)', padding: '1px 3px', borderRadius: '3px', fontSize: '0.9em' }}>{m[4]}</code>);
            cursor = m.index + m[0].length;
          }
          if (cursor < part.length) out.push(<span key={cursor}>{part.slice(cursor)}</span>);
          return out;
        })}
      </span>
    ));
  };

  const insertSnippetFormatting = (fmt) => {
    const ta = snippetContentRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const text = snippetForm.content;
    const sel = text.substring(s, e);
    const wrapMap = { bold: '*', italic: '_', strikethrough: '~', monospace: '`' };
    let rep;
    if (wrapMap[fmt]) rep = sel ? `${wrapMap[fmt]}${sel}${wrapMap[fmt]}` : `${wrapMap[fmt]}${wrapMap[fmt]}`;
    else if (fmt === 'bullet')    rep = sel ? `• ${sel}` : '• ';
    else if (fmt === 'numbered')  rep = sel ? `1. ${sel}` : '1. ';
    else if (fmt === 'link')      return;
    else return;
    const newContent = text.substring(0, s) + rep + text.substring(e);
    setSnippetForm(f => ({ ...f, content: newContent }));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + rep.length, s + rep.length); }, 0);
  };

  const insertEmoji = (emojiData) => {
    const ta = snippetContentRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const emoji = emojiData.emoji || emojiData;
    const newContent = snippetForm.content.substring(0, pos) + emoji + snippetForm.content.substring(pos);
    setSnippetForm(f => ({ ...f, content: newContent }));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(pos + emoji.length, pos + emoji.length); }, 0);
    setShowEmojiPicker(false);
  };

  const openLinkModal = () => {
    const ta = snippetContentRef.current;
    setLinkModalText(ta ? snippetForm.content.substring(ta.selectionStart, ta.selectionEnd) : '');
    setLinkModalUrl('');
    setShowLinkModal(true);
  };

  const applyLink = () => {
    if (!linkModalUrl.trim()) return;
    const ta = snippetContentRef.current;
    const pos = ta ? ta.selectionStart : snippetForm.content.length;
    const rep = (linkModalText.trim() || linkModalUrl.trim()) + ' ' + linkModalUrl.trim();
    const newContent = snippetForm.content.substring(0, pos) + rep + snippetForm.content.substring(pos);
    setSnippetForm(f => ({ ...f, content: newContent }));
    setShowLinkModal(false);
  };

  const detectMediaUrlType = (url) => {
    if (!url) return null;
    const u = url.toLowerCase();
    if (/\.(jpe?g|png|gif|webp)(\?|$)/.test(u)) return 'image';
    if (/\.(mp4|webm|mov)(\?|$)/.test(u)) return 'video';
    if (/\.(ogg|mp3|m4a|aac)(\?|$)/.test(u)) return 'audio';
    return null;
  };

  const handleContentPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData)?.getData('text');
    if (!pasted) return;
    const type = detectMediaUrlType(pasted);
    if (type) { e.preventDefault(); setSmartUrlModal({ url: pasted.trim(), type }); }
  };

  const applySmartUrl = () => {
    if (!smartUrlModal) return;
    const { url, type } = smartUrlModal;
    setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, [type]: { url } } }));
    setSmartUrlModal(null);
  };

  const handleMediaUpload = async (file, type) => {
    if (!file.type.startsWith(type + '/')) { toast.error(language === 'ar' ? 'نوع الملف غير مدعوم' : `Use ${type} file`); return; }
    setMediaUploading(prev => ({ ...prev, [type]: true }));
    try {
      const res = await uploadWhatsAppMedia(file);
      const url = res.data?.url;
      if (url) { setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, [type]: { url } } })); toast.success(language === 'ar' ? 'تم الرفع' : 'Uploaded'); }
      else throw new Error('No URL');
    } catch { toast.error(language === 'ar' ? 'فشل الرفع' : 'Upload failed'); }
    finally { setMediaUploading(prev => ({ ...prev, [type]: false })); }
  };

  const handleMediaDrop = (e, type) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer?.files?.[0]; if (f) handleMediaUpload(f, type); };

  const saveSnippet = async () => {
    const mt = snippetForm.message_type || 'text';
    const hasMeta = mt === 'image' ? !!snippetForm.media_metadata?.image?.url
      : mt === 'video' ? !!snippetForm.media_metadata?.video?.url
      : mt === 'audio' ? !!snippetForm.media_metadata?.audio?.url
      : mt === 'location' ? snippetForm.media_metadata?.location?.lat != null : false;
    if (!snippetForm.label.trim()) { toast.error(language === 'ar' ? 'أدخل التسمية' : 'Enter a label'); return; }
    if (mt === 'text' && !snippetForm.content.trim()) { toast.error(language === 'ar' ? 'أدخل المحتوى' : 'Enter content'); return; }
    if (mt !== 'text' && !hasMeta) { toast.error(language === 'ar' ? 'أرفق الوسائط المطلوبة' : 'Attach required media'); return; }
    try {
      setSnippetsSaving(true);
      const body = { ...snippetForm, category: normalizeSnippetCategoryForSave(snippetForm.category) };
      if (editingSnippetId) { await updateWhatsAppSnippet(editingSnippetId, body); toast.success(language === 'ar' ? 'تم التحديث' : 'Updated'); }
      else { await createWhatsAppSnippet(body); toast.success(language === 'ar' ? 'تمت الإضافة' : 'Added'); }
      setSnippetForm({ ...EMPTY_SNIPPET });
      setEditingSnippetId(null);
      await loadSnippets();
    } catch (err) {
      toast.error(err?.response?.data?.error || (language === 'ar' ? 'فشل الحفظ' : 'Save failed'));
    } finally { setSnippetsSaving(false); }
  };

  const cloneSnippet = async (s) => {
    try {
      setSnippetsSaving(true);
      await createWhatsAppSnippet({ ...s, label: (language === 'ar' ? 'نسخة من ' : 'Copy of ') + s.label });
      toast.success(language === 'ar' ? 'تم النسخ' : 'Cloned');
      await loadSnippets();
    } catch { toast.error(language === 'ar' ? 'فشل النسخ' : 'Clone failed'); }
    finally { setSnippetsSaving(false); }
  };

  // ── Interactive Presets helpers ────────────────────────────────────────────
  const loadPresets = async () => {
    try {
      setPresetsLoading(true);
      const res = await getWhatsAppInteractivePresets();
      setPresets(Array.isArray(res.data?.presets) ? res.data.presets : []);
    } catch { toast.error(language === 'ar' ? 'تعذر التحميل' : 'Failed to load presets'); }
    finally { setPresetsLoading(false); }
  };

  const loadOrgTagSuggestions = async () => {
    try {
      const res = await getTagSuggestions();
      setOrgTagSuggestions(Array.isArray(res.data?.tags) ? res.data.tags : []);
    } catch { /* non-critical, silently ignore */ }
  };

  const savePreset = async () => {
    if (!presetForm.label.trim()) { toast.error(language === 'ar' ? 'أدخل التسمية' : 'Enter a label'); return; }
    if (!presetForm.body.trim()) { toast.error(language === 'ar' ? 'أدخل نص الرسالة' : 'Enter message body'); return; }
    if ((presetForm.body || '').length > 1024) { toast.error(language === 'ar' ? 'النص طويل جداً (1024)' : 'Body too long (1024 max)'); return; }
    if (presetForm.preset_type === 'list') {
      const lb = String(presetForm.listButton || '').trim();
      if (!lb) { toast.error(language === 'ar' ? 'أدخل نص زر القائمة' : 'Enter list menu button text'); return; }
      const n = countListRows(presetForm.sections);
      if (n < 1) { toast.error(language === 'ar' ? 'أضف صفاً واحداً على الأقل' : 'Add at least one list row with a title'); return; }
      if (n > MAX_LIST_ROWS) { toast.error(language === 'ar' ? `بحد أقصى ${MAX_LIST_ROWS} صفوف` : `At most ${MAX_LIST_ROWS} rows`); return; }
    } else {
      const validButtons = presetForm.buttons.filter(b => b.title.trim());
      if (validButtons.length === 0) { toast.error(language === 'ar' ? 'أضف زراً واحداً على الأقل' : 'Add at least one button'); return; }
    }
    const stored = formToStoredButtons(presetForm);
    const payload = {
      label: presetForm.label,
      body: presetForm.body,
      preset_type: presetForm.preset_type || 'button',
      buttons: stored,
    };
    try {
      setPresetsSaving(true);
      if (editingPresetId) { await updateWhatsAppInteractivePreset(editingPresetId, payload); toast.success(language === 'ar' ? 'تم التحديث' : 'Updated'); }
      else { await createWhatsAppInteractivePreset(payload); toast.success(language === 'ar' ? 'تمت الإضافة' : 'Added'); }
      setPresetForm({ ...EMPTY_INTERACTIVE_PRESET_FORM });
      setEditingPresetId(null);
      await loadPresets();
    } catch (err) {
      toast.error(err?.response?.data?.error || (language === 'ar' ? 'فشل الحفظ' : 'Save failed'));
    } finally { setPresetsSaving(false); }
  };

  const clonePreset = async (p) => {
    try {
      setPresetsSaving(true);
      const pt = p.preset_type || 'button';
      const stored = pt === 'list' && p.buttons && typeof p.buttons === 'object' && !Array.isArray(p.buttons)
        ? p.buttons
        : (Array.isArray(p.buttons) ? p.buttons : (typeof p.buttons === 'string' ? (() => { try { return JSON.parse(p.buttons); } catch { return pt === 'list' ? {} : []; } })() : (pt === 'list' ? {} : [])));
      await createWhatsAppInteractivePreset({ label: (language === 'ar' ? 'نسخة من ' : 'Copy of ') + p.label, body: p.body, preset_type: pt, buttons: stored });
      toast.success(language === 'ar' ? 'تم النسخ' : 'Cloned');
      await loadPresets();
    } catch { toast.error(language === 'ar' ? 'فشل النسخ' : 'Clone failed'); }
    finally { setPresetsSaving(false); }
  };

  const updateButton = (idx, field, value) => {
    const btns = [...presetForm.buttons];
    btns[idx] = { ...btns[idx], [field]: value };
    setPresetForm(f => ({ ...f, buttons: btns }));
  };

  const updateButtonRule = (idx, field, value) => {
    const btns = [...presetForm.buttons];
    const current = btns[idx].rule && typeof btns[idx].rule === 'object' ? btns[idx].rule : { ...EMPTY_RULE };
    btns[idx] = { ...btns[idx], rule: { ...current, [field]: value } };
    setPresetForm(f => ({ ...f, buttons: btns }));
  };

  const updateListRowRule = (si, ri, field, value) => {
    setPresetForm((f) => {
      const sections = f.sections.map((s, sIdx) => {
        if (sIdx !== si) return s;
        const rows = s.rows.map((r, rIdx) => {
          if (rIdx !== ri) return r;
          const current = r.rule && typeof r.rule === 'object' ? r.rule : { ...EMPTY_RULE };
          return { ...r, rule: { ...current, [field]: value } };
        });
        return { ...s, rows };
      });
      return { ...f, sections };
    });
  };

  const addButton = () => {
    if (presetForm.buttons.length >= 3) return;
    setPresetForm(f => ({ ...f, buttons: [...f.buttons, { id: `btn_${f.buttons.length + 1}`, title: '', rule: null }] }));
  };

  const removeButton = (idx) => {
    setPresetForm(f => ({ ...f, buttons: f.buttons.filter((_, i) => i !== idx) }));
  };

  const listRowTotal = () => countListRows(presetForm.sections);

  const addListRow = (sectionIdx) => {
    if (listRowTotal() >= MAX_LIST_ROWS) return;
    setPresetForm((f) => {
      const sections = [...f.sections];
      const rows = [...(sections[sectionIdx]?.rows || []), { id: `row_${Date.now()}`, title: '', description: '', rule: null }];
      sections[sectionIdx] = { ...sections[sectionIdx], rows };
      return { ...f, sections };
    });
  };

  const removeListRow = (sectionIdx, rowIdx) => {
    setPresetForm((f) => {
      const sections = f.sections.map((s, si) => {
        if (si !== sectionIdx) return s;
        return { ...s, rows: s.rows.filter((_, ri) => ri !== rowIdx) };
      });
      return { ...f, sections };
    });
  };

  const updateListRow = (sectionIdx, rowIdx, field, value) => {
    setPresetForm((f) => {
      const sections = f.sections.map((s, si) => {
        if (si !== sectionIdx) return s;
        const rows = s.rows.map((r, ri) => (ri === rowIdx ? { ...r, [field]: value } : r));
        return { ...s, rows };
      });
      return { ...f, sections };
    });
  };

  const addListSection = () => {
    setPresetForm((f) => ({
      ...f,
      sections: [...f.sections, { title: '', rows: [{ id: `row_${Date.now()}`, title: '', description: '', rule: null }] }],
    }));
  };

  const removeListSection = (sectionIdx) => {
    setPresetForm((f) => ({
      ...f,
      sections: f.sections.length > 1 ? f.sections.filter((_, i) => i !== sectionIdx) : f.sections,
    }));
  };

  const updateSectionTitle = (sectionIdx, value) => {
    setPresetForm((f) => {
      const sections = f.sections.map((s, i) => (i === sectionIdx ? { ...s, title: value } : s));
      return { ...f, sections };
    });
  };

  // ── Shared toolbar button ─────────────────────────────────────────────────
  const toolbarBtn = (onClick, icon, title) => (
    <button type="button" onClick={onClick} title={title} style={{ padding: '5px 8px', border: 'none', background: 'transparent', borderRadius: '5px', cursor: 'pointer', color: '#374151', fontSize: '13px' }}
      onMouseEnter={e => e.currentTarget.style.background = '#e5e7eb'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >{icon}</button>
  );

  // ── Sub-tab style (match wa-subtab sizing in WhatsAppCampaigns) ───────────
  const tabStyle = (active) => ({
    padding: '10px 20px', fontSize: '14px', fontWeight: 600, border: 'none', borderRadius: '9999px', cursor: 'pointer',
    background: active ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'transparent',
    color: active ? '#fff' : '#64748b', transition: 'all 0.2s ease',
    boxShadow: active ? '0 2px 8px rgba(99,102,241,0.3)' : 'none',
    display: 'flex', alignItems: 'center', gap: '8px'
  });

  // ── Type map for snippet badges ───────────────────────────────────────────
  const TYPE_MAP = {
    text:     { icon: '💬', color: '#e0f2fe', text: '#0369a1' },
    image:    { icon: '🖼️', color: '#fef3c7', text: '#92400e' },
    video:    { icon: '🎬', color: '#ede9fe', text: '#6d28d9' },
    audio:    { icon: '🎵', color: '#fce7f3', text: '#9d174d' },
    location: { icon: '📍', color: '#dcfce7', text: '#166534' },
  };

  const snippetCategoryOptions = useMemo(() => {
    const fromDb = [...new Set(snippets.map((s) => String(s.category || 'general').trim()).filter(Boolean))];
    const merged = [...new Set([...SNIPPET_PRESET_KEYS, ...fromDb])];
    const loc = language === 'ar' ? 'ar' : 'en';
    return merged.sort((a, b) => formatSnippetCategoryLabel(a, language).localeCompare(formatSnippetCategoryLabel(b, language), loc));
  }, [snippets, language]);

  const filteredSnippets = useMemo(() => {
    let list = snippets;
    if (snippetCategoryFilter !== 'all') {
      list = list.filter((s) => String(s.category || 'general').trim().toLowerCase() === String(snippetCategoryFilter).trim().toLowerCase());
    }
    const q = snippetListQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) =>
      (s.label || '').toLowerCase().includes(q) ||
      (s.category || '').toLowerCase().includes(q) ||
      (s.content || '').toLowerCase().includes(q)
    );
  }, [snippets, snippetListQuery, snippetCategoryFilter]);

  const snippetCategorySelectValue = isPresetSnippetCategory(snippetForm.category)
    ? String(snippetForm.category).trim().toLowerCase()
    : '__custom__';

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="wa-animate-in" style={{ padding: '24px', maxWidth: '100%', boxSizing: 'border-box' }}>

      {/* Sub-tab navigation */}
      <div className="wa-subtabs" style={{ display: 'flex', gap: '6px', marginBottom: '20px', padding: '4px', background: '#f1f5f9', borderRadius: '9999px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
        <button
          type="button"
          className="wa-subtab"
          onClick={() => setSubTab('quickreplies')}
          style={tabStyle(subTab === 'quickreplies')}
          onMouseEnter={(e) => { if (subTab !== 'quickreplies') { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.color = '#4f46e5'; } }}
          onMouseLeave={(e) => { if (subTab !== 'quickreplies') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
        >
          💬 {language === 'ar' ? 'الردود الجاهزة' : 'Quick Replies'}
        </button>
        <button
          type="button"
          className="wa-subtab"
          onClick={() => setSubTab('interactive')}
          style={tabStyle(subTab === 'interactive')}
          onMouseEnter={(e) => { if (subTab !== 'interactive') { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.color = '#4f46e5'; } }}
          onMouseLeave={(e) => { if (subTab !== 'interactive') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
        >
          🔘 {language === 'ar' ? 'الرسائل التفاعلية' : 'Interactive Messages'}
        </button>
      </div>

      {/* Page title — subtitle below title so RTL does not merge counts with the description */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FaTools style={{ color: '#6366f1' }} />
          {language === 'ar' ? 'أدوات واتساب' : 'WhatsApp Tools'}
        </h1>
        <p
          style={{
            margin: '10px 0 0 0',
            fontSize: '14px',
            lineHeight: 1.55,
            color: '#475569',
            fontWeight: 500,
            maxWidth: '52ch',
          }}
        >
          {subTab === 'interactive'
            ? (language === 'ar'
              ? 'هنا تُنشئ وتعدّل قوالب رسائل تفاعلية — أزرار أو قائمة — لاستخدامها في المحادثة فوراً.'
              : 'Create and edit interactive message templates—buttons or a list—for use in conversations right away.')
            : (language === 'ar'
              ? 'هنا تُنشئ وتعدّل النصوص والوسائط الجاهزة لاستخدامها في المحادثة فوراً.'
              : 'Manage saved text and media snippets you paste into chats — your quick replies library.')}
        </p>
      </div>

      {/* ════════════════ QUICK REPLIES TAB ════════════════ */}
      {subTab === 'quickreplies' && (
        <div style={{ width: '100%', maxWidth: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px 24px' }}>
          <div style={{ marginBottom: canManage ? '14px' : '16px' }}>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
              {language === 'ar' ? 'الردود الجاهزة' : 'Quick Replies'}
            </h3>
          </div>

          {canManage && (
            <div
              className="wa-qr-layout-hint"
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              role="region"
              aria-label={language === 'ar' ? 'دليل التخطيط: المكتبة ثم النموذج' : 'Layout guide: library, then editor'}
            >
              <div className="wa-qr-layout-hint__step">
                <span className="wa-qr-layout-hint__badge" aria-hidden>1</span>
                <span className="wa-qr-layout-hint__step-icon" aria-hidden>
                  <FaLayerGroup />
                </span>
                <div className="wa-qr-layout-hint__text">
                  <div className="wa-qr-layout-hint__title">
                    {language === 'ar' ? 'المكتبة' : 'Library'}
                  </div>
                  <div className="wa-qr-layout-hint__desc">
                    {language === 'ar' ? 'مرّر القائمة وابحث واختر رداً للتعديل.' : 'Scroll the list, search, and pick a reply to edit.'}
                  </div>
                </div>
              </div>
              <div className="wa-qr-layout-hint__bridge" aria-hidden="true">
                <span className="wa-qr-layout-hint__bridge-line" />
                <span className="wa-qr-layout-hint__bridge-icon wa-qr-layout-hint__bridge-icon--wide">
                  {language === 'ar' ? <FaChevronLeft /> : <FaChevronRight />}
                </span>
                <span className="wa-qr-layout-hint__bridge-icon wa-qr-layout-hint__bridge-icon--narrow">
                  <FaChevronDown />
                </span>
                <span className="wa-qr-layout-hint__bridge-line" />
              </div>
              <div className="wa-qr-layout-hint__step">
                <span className="wa-qr-layout-hint__badge" aria-hidden>2</span>
                <span className="wa-qr-layout-hint__step-icon" aria-hidden>
                  <FaEdit />
                </span>
                <div className="wa-qr-layout-hint__text">
                  <div className="wa-qr-layout-hint__title">
                    {language === 'ar' ? 'النموذج' : 'Editor'}
                  </div>
                  <div className="wa-qr-layout-hint__desc">
                    {language === 'ar'
                      ? 'أضف رداً جديداً أو عدّل الموجود — بجانب المكتبة على الشاشات العريضة، وأسفلها على الجوال.'
                      : 'Add a new reply or edit here — beside the library on wide screens, below it on mobile.'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!canManage && (
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
              {language === 'ar' ? 'ردود محفوظة للاستخدام في المحادثات.' : 'Saved replies for use in chats.'}
            </p>
          )}

          {snippetsLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</div>
          ) : (
            <div className="wa-quick-replies-layout">
              {/* Library: search + scrollable list */}
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {snippets.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                    <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px 12px' }}>
                      <FaSearch style={{ color: '#94a3b8', flexShrink: 0 }} aria-hidden />
                      <input
                        className="wa-input"
                        type="search"
                        value={snippetListQuery}
                        onChange={(e) => setSnippetListQuery(e.target.value)}
                        placeholder={language === 'ar' ? 'بحث في التسمية أو المحتوى...' : 'Search labels or content...'}
                        style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '13px', color: '#0f172a', boxShadow: 'none', outline: 'none' }}
                      />
                    </div>
                    <select
                      className="wa-input"
                      value={snippetCategoryFilter}
                      onChange={(e) => setSnippetCategoryFilter(e.target.value)}
                      style={{ flex: '0 1 min(220px, 100%)', fontSize: '13px' }}
                    >
                      <option value="all">{language === 'ar' ? 'كل التصنيفات' : 'All categories'}</option>
                      {snippetCategoryOptions.map((cat) => (
                        <option key={cat} value={cat}>{formatSnippetCategoryLabel(cat, language)}</option>
                      ))}
                    </select>
                    <span
                      role="status"
                      aria-label={
                        language === 'ar'
                          ? (filteredSnippets.length === snippets.length
                            ? `عدد النصوص ${filteredSnippets.length}`
                            : `عرض ${filteredSnippets.length} من أصل ${snippets.length}`)
                          : (filteredSnippets.length === snippets.length
                            ? `${filteredSnippets.length} snippets`
                            : `Showing ${filteredSnippets.length} of ${snippets.length}`)
                      }
                      style={{
                        fontSize: '12px',
                        color: '#334155',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        background: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '6px 10px',
                      }}
                    >
                      {language === 'ar' ? (
                        filteredSnippets.length === snippets.length ? (
                          <>
                            <span style={{ fontWeight: 500, color: '#64748b' }}>عدد النصوص: </span>
                            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{filteredSnippets.length}</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontWeight: 500, color: '#64748b' }}>عرض </span>
                            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{filteredSnippets.length}</span>
                            <span style={{ fontWeight: 500, color: '#64748b' }}> من </span>
                            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{snippets.length}</span>
                          </>
                        )
                      ) : (
                        filteredSnippets.length === snippets.length
                          ? `${filteredSnippets.length} ${filteredSnippets.length === 1 ? 'snippet' : 'snippets'}`
                          : `Showing ${filteredSnippets.length} of ${snippets.length}`
                      )}
                    </span>
                  </div>
                )}

                {snippets.length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '12px', color: '#64748b' }}>
                    {canManage
                      ? (language === 'ar' ? 'لا توجد ردود جاهزة بعد. أضف واحداً من النموذج →' : 'No quick replies yet. Add one using the form →')
                      : (language === 'ar' ? 'لا توجد ردود جاهزة.' : 'No quick replies yet.')}
                  </div>
                ) : filteredSnippets.length === 0 ? (
                  <div style={{ padding: '28px', textAlign: 'center', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', color: '#92400e', fontSize: '13px' }}>
                    {language === 'ar' ? 'لا توجد نتائج لبحثك أو التصفية.' : 'No replies match your search or filter.'}
                  </div>
                ) : (
                  <div
                    className="wa-snippets-scrollbar"
                    style={{
                      maxHeight: 'min(72vh, 680px)',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      WebkitOverflowScrolling: 'touch',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      background: '#fafafa',
                      padding: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    {filteredSnippets.map((s) => {
                      const badge = TYPE_MAP[s.message_type || 'text'] || TYPE_MAP.text;
                      const isExpanded = expandedSnippets.has(s.id);
                      const isLong = s.content && s.content.length > 200;
                      return (
                        <div key={s.id} style={{ background: editingSnippetId === s.id ? '#fef3c7' : '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px', padding: '14px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', background: '#f1f5f9', padding: '3px 9px', borderRadius: '6px' }}>{formatSnippetCategoryLabel(s.category, language)}</span>
                              <span style={{ fontSize: '10px', fontWeight: 700, color: badge.text, background: badge.color, padding: '2px 7px', borderRadius: '4px' }}>{badge.icon} {s.message_type || 'text'}</span>
                              <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>{s.label}</span>
                              {s.usage_count > 0 && <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: '20px' }}>{language === 'ar' ? `استُخدم ${s.usage_count}×` : `Used ${s.usage_count}×`}</span>}
                            </div>
                            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: isLong && !isExpanded ? '90px' : 'none', overflow: isLong && !isExpanded ? 'hidden' : 'visible', position: 'relative' }}>
                              {renderFormattedText(s.content)}
                              {isLong && !isExpanded && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '36px', background: 'linear-gradient(transparent, #fff)' }} />}
                            </div>
                            {isLong && (
                              <button type="button" onClick={() => setExpandedSnippets((prev) => { const n = new Set(prev); isExpanded ? n.delete(s.id) : n.add(s.id); return n; })}
                                style={{ marginTop: '6px', padding: '3px 10px', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#6366f1', fontWeight: 600 }}>
                                {isExpanded ? (language === 'ar' ? 'إظهار أقل' : 'Show less') : (language === 'ar' ? 'إظهار المزيد' : 'Show more')}
                              </button>
                            )}
                          </div>
                          {canManage && (
                            <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => { setEditingSnippetId(s.id); setSnippetForm({ category: s.category || 'general', label: s.label, content: s.content || '', message_type: s.message_type || 'text', media_metadata: s.media_metadata || {} }); setTimeout(() => document.getElementById('snippet-form-tools')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }}
                                style={{ padding: '5px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                                {language === 'ar' ? 'تعديل' : 'Edit'}
                              </button>
                              <button type="button" onClick={() => cloneSnippet(s)} disabled={snippetsSaving}
                                style={{ padding: '5px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#166534', fontWeight: 600 }}>
                                {language === 'ar' ? 'نسخ' : 'Clone'}
                              </button>
                              <button type="button" onClick={async () => { if (!window.confirm(language === 'ar' ? 'حذف هذا الرد؟' : 'Delete this reply?')) return; try { setSnippetsSaving(true); await deleteWhatsAppSnippet(s.id); toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted'); await loadSnippets(); } catch { toast.error(language === 'ar' ? 'فشل الحذف' : 'Delete failed'); } finally { setSnippetsSaving(false); } }}
                                disabled={snippetsSaving} style={{ padding: '5px 10px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#991b1b', fontWeight: 600 }}>
                                {language === 'ar' ? 'حذف' : 'Delete'}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Create / Edit Form — visible only to users with wa_tools_manage */}
              {canManage && (
            <div id="snippet-form-tools" className="wa-snippets-scrollbar" style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '20px 22px', minWidth: 0, maxHeight: 'min(90vh, 980px)', overflowY: 'auto', alignSelf: 'stretch' }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#334155', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>{editingSnippetId ? '✎' : '+'}</span>
                {editingSnippetId ? (language === 'ar' ? 'تعديل رد جاهز' : 'Edit Quick Reply') : (language === 'ar' ? 'إضافة رد جاهز' : 'Add Quick Reply')}
              </div>

              <div style={{ display: 'grid', gap: '16px' }}>
                {/* Row: category + label */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{language === 'ar' ? 'التصنيف' : 'Category'}</label>
                    <select
                      className="wa-input"
                      value={snippetCategorySelectValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__custom__') setSnippetForm((f) => ({ ...f, category: '' }));
                        else setSnippetForm((f) => ({ ...f, category: v }));
                      }}
                      style={{ width: '100%', fontSize: '14px' }}
                    >
                      {SNIPPET_PRESET_KEYS.map((key) => (
                        <option key={key} value={key}>{formatSnippetCategoryLabel(key, language)}</option>
                      ))}
                      <option value="__custom__">{language === 'ar' ? 'تصنيف مخصص…' : 'Custom category…'}</option>
                    </select>
                    {snippetCategorySelectValue === '__custom__' && (
                      <div style={{ marginTop: '10px' }}>
                        <input
                          className="wa-input"
                          placeholder={language === 'ar' ? 'اكتب اسماً للتصنيف (مثال: مبيعات، عروض)' : 'Type a name (e.g. sales, offers — any language)'}
                          value={snippetForm.category}
                          onChange={(e) => setSnippetForm((f) => ({ ...f, category: e.target.value }))}
                          style={{ width: '100%', fontSize: '14px' }}
                          maxLength={64}
                          dir="auto"
                        />
                        <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#64748b', lineHeight: 1.45 }}>
                          {language === 'ar'
                            ? 'يظهر في القائمة والتصفية بنفس الاسم. يمكنك إعادة استخدامه لاحقاً.'
                            : 'This name appears in the list and filter. Reuse it for other replies to group them.'}
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{language === 'ar' ? 'التسمية *' : 'Label *'}</label>
                    <input className="wa-input" placeholder={language === 'ar' ? 'مثال: تحية أولية' : 'e.g. Initial Greeting'} value={snippetForm.label} onChange={e => setSnippetForm(f => ({ ...f, label: e.target.value }))} style={{ width: '100%', fontSize: '14px' }} />
                  </div>
                </div>

                {/* Message type selector */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{language === 'ar' ? 'نوع الرسالة' : 'Message Type'}</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { v: 'text',     icon: '💬', ar: 'نص',   en: 'Text',     d: language === 'ar' ? 'نص + تنسيق' : 'Text + formatting' },
                      { v: 'image',    icon: '🖼️', ar: 'صورة', en: 'Image',    d: language === 'ar' ? 'صورة + تعليق' : 'Image + caption' },
                      { v: 'video',    icon: '🎬', ar: 'فيديو', en: 'Video',   d: language === 'ar' ? 'فيديو + تعليق' : 'Video + caption' },
                      { v: 'audio',    icon: '🎵', ar: 'صوت',  en: 'Audio',    d: language === 'ar' ? 'رسالة صوتية' : 'Voice / audio' },
                      { v: 'location', icon: '📍', ar: 'موقع', en: 'Location', d: language === 'ar' ? 'بطاقة موقع' : 'Location card' },
                    ].map(t => {
                      const active = (snippetForm.message_type || 'text') === t.v;
                      return (
                        <button key={t.v} type="button" onClick={() => setSnippetForm(f => ({ ...f, message_type: t.v, media_metadata: {}, content: '' }))}
                          style={{ flex: '1 1 100px', padding: '9px 10px', border: `2px solid ${active ? '#6366f1' : '#e2e8f0'}`, borderRadius: '10px', background: active ? '#eef2ff' : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
                          <div style={{ fontSize: '18px', marginBottom: '3px' }}>{t.icon}</div>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: active ? '#4f46e5' : '#374151' }}>{language === 'ar' ? t.ar : t.en}</div>
                          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{t.d}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Type-specific fields */}
                {(() => {
                  const mt = snippetForm.message_type || 'text';
                  const ruleMap = {
                    text:     { bg: '#e0f2fe', border: '#bae6fd', icon: 'ℹ️', msg: language === 'ar' ? 'نص فقط — حتى 4,096 حرفاً.' : 'Text only — up to 4,096 chars.' },
                    image:    { bg: '#fef3c7', border: '#fde68a', icon: '📐', msg: language === 'ar' ? 'JPEG/PNG/WebP — حد 5 ميغابايت. التعليق اختياري.' : 'JPEG/PNG/WebP — max 5 MB. Caption optional.' },
                    video:    { bg: '#fef3c7', border: '#fde68a', icon: '📐', msg: language === 'ar' ? 'MP4/3GPP — حد 16 ميغابايت. التعليق اختياري.' : 'MP4/3GPP — max 16 MB. Caption optional.' },
                    audio:    { bg: '#fff7ed', border: '#fed7aa', icon: '⚠️', msg: language === 'ar' ? 'لا يدعم واتساب إضافة تعليق على الرسائل الصوتية.' : 'WhatsApp audio does not support captions.' },
                    location: { bg: '#f0fdf4', border: '#bbf7d0', icon: '🗺️', msg: language === 'ar' ? 'يُرسل كبطاقة خريطة. لا يمكن دمجه مع نص.' : 'Sent as a map card. Cannot be combined with text.' },
                  };
                  const rule = ruleMap[mt];
                  const len = (snippetForm.content || '').length;
                  const textLimit = mt === 'text' ? 4096 : 1024;
                  const overLimit = len > textLimit;
                  const nearLimit = len > textLimit * 0.85;

                  return (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {/* Rule banner */}
                      <div style={{ padding: '9px 13px', borderRadius: '8px', background: rule.bg, border: `1px solid ${rule.border}`, fontSize: '12px', color: '#374151', display: 'flex', gap: '8px' }}>
                        <span>{rule.icon}</span><span>{rule.msg}</span>
                      </div>

                      {/* TEXT */}
                      {mt === 'text' && (
                        <div style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '5px 7px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0', borderBottom: 'none' }}>
                            {toolbarBtn(() => setShowEmojiPicker(p => !p), <FaSmile />, 'Emoji')}
                            {showEmojiPicker && <div style={{ position: 'absolute', top: '34px', zIndex: 1100 }}><EmojiPicker onEmojiClick={insertEmoji} width={280} height={320} /></div>}
                            {[['bold',<FaBold />],['italic',<FaItalic />],['strikethrough',<FaStrikethrough />],['monospace',<FaCode />]].map(([f,i]) => toolbarBtn(() => insertSnippetFormatting(f), i, f))}
                            {toolbarBtn(openLinkModal, <FaLink />, 'Link')}
                            {toolbarBtn(() => insertSnippetFormatting('bullet'), <FaListUl />, 'Bullet')}
                            {toolbarBtn(() => insertSnippetFormatting('numbered'), <FaListOl />, 'Numbered')}
                            <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 600, color: overLimit ? '#dc2626' : nearLimit ? '#d97706' : '#9ca3af', alignSelf: 'center', paddingRight: '4px' }}>{len.toLocaleString()} / {textLimit.toLocaleString()}</span>
                          </div>
                          <textarea ref={snippetContentRef} className="wa-input" placeholder={language === 'ar' ? 'اكتب نص الرد...' : 'Type reply text...'} value={snippetForm.content} onChange={e => setSnippetForm(f => ({ ...f, content: e.target.value }))} onPaste={handleContentPaste} style={{ width: '100%', minHeight: '110px', resize: 'vertical', fontFamily: 'inherit', borderRadius: '0 0 8px 8px', borderTop: 'none', direction: detectTextDirection(snippetForm.content), borderColor: overLimit ? '#dc2626' : undefined }} rows={4} spellCheck lang={/[\u0600-\u06FF]/.test(snippetForm.content || '') ? 'ar' : 'en-US'} dir={detectTextDirection(snippetForm.content)} />
                          {overLimit && <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '3px' }}>{language === 'ar' ? `تجاوزت الحد بـ ${len - textLimit} حرف` : `${len - textLimit} chars over limit`}</div>}
                        </div>
                      )}

                      {/* IMAGE / VIDEO */}
                      {(mt === 'image' || mt === 'video') && (
                        <div style={{ display: 'grid', gap: '10px' }}>
                          <div onDrop={e => handleMediaDrop(e, mt)} onDragOver={e => { e.preventDefault(); e.stopPropagation(); }} onClick={() => document.getElementById(`file-tools-${mt}`)?.click()}
                            style={{ border: `2px dashed ${snippetForm.media_metadata?.[mt]?.url ? '#22c55e' : '#d1d5db'}`, borderRadius: '10px', padding: '20px', textAlign: 'center', cursor: 'pointer', background: snippetForm.media_metadata?.[mt]?.url ? '#f0fdf4' : '#f9fafb' }}>
                            <input id={`file-tools-${mt}`} type="file" accept={mt === 'image' ? 'image/*' : 'video/*'} style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleMediaUpload(f, mt); e.target.value = ''; }} />
                            {mediaUploading[mt] ? <div style={{ color: '#6b7280', fontSize: '13px' }}>{language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}</div>
                              : snippetForm.media_metadata?.[mt]?.url ? (
                                <div>{mt === 'image' && <img src={snippetForm.media_metadata.image.url} alt="" style={{ maxHeight: '110px', maxWidth: '100%', borderRadius: '6px', marginBottom: '6px' }} onError={e => e.target.style.display = 'none'} />}
                                  {mt === 'video' && <div style={{ fontSize: '28px', marginBottom: '6px' }}>🎬</div>}
                                  <div style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>{language === 'ar' ? 'تم الرفع' : 'Uploaded'}</div>
                                  <button type="button" onClick={e => { e.stopPropagation(); setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, [mt]: undefined } })); }} style={{ marginTop: '5px', fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>{language === 'ar' ? 'إزالة' : 'Remove'}</button>
                                </div>
                              ) : (
                                <div><FaUpload style={{ fontSize: '26px', color: '#9ca3af', marginBottom: '6px' }} />
                                  <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600 }}>{language === 'ar' ? 'اسحب أو انقر للرفع' : 'Drag or click to upload'}</div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{mt === 'image' ? 'JPEG · PNG · WebP — max 5 MB' : 'MP4 · 3GPP — max 16 MB'}</div>
                                </div>
                              )}
                          </div>
                          <input className="wa-input" placeholder={language === 'ar' ? 'أو الصق رابطاً' : 'Or paste URL'} value={snippetForm.media_metadata?.[mt]?.url || ''} onChange={e => setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, [mt]: e.target.value ? { url: e.target.value } : undefined } }))} style={{ fontSize: '12px' }} />
                          {/* Caption */}
                          <div style={{ position: 'relative' }}>
                            <div style={{ display: 'flex', gap: '2px', padding: '5px 7px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0', borderBottom: 'none' }}>
                              {toolbarBtn(() => setShowEmojiPicker(p => !p), <FaSmile />, 'Emoji')}
                              {showEmojiPicker && <div style={{ position: 'absolute', top: '34px', zIndex: 1100 }}><EmojiPicker onEmojiClick={insertEmoji} width={280} height={300} /></div>}
                              {[['bold',<FaBold />],['italic',<FaItalic />]].map(([fmt,i]) => toolbarBtn(() => insertSnippetFormatting(fmt), i, fmt))}
                              <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 600, color: overLimit ? '#dc2626' : nearLimit ? '#d97706' : '#9ca3af', alignSelf: 'center', paddingRight: '4px' }}>{len} / {textLimit}</span>
                            </div>
                            <textarea ref={snippetContentRef} className="wa-input" placeholder={language === 'ar' ? 'تعليق اختياري...' : 'Optional caption...'} value={snippetForm.content} onChange={e => setSnippetForm(f => ({ ...f, content: e.target.value }))} style={{ width: '100%', minHeight: '64px', resize: 'vertical', fontFamily: 'inherit', borderRadius: '0 0 8px 8px', borderTop: 'none', direction: detectTextDirection(snippetForm.content) }} rows={2} spellCheck />
                          </div>
                        </div>
                      )}

                      {/* AUDIO */}
                      {mt === 'audio' && (
                        <div>
                          <div onDrop={e => handleMediaDrop(e, 'audio')} onDragOver={e => { e.preventDefault(); e.stopPropagation(); }} onClick={() => document.getElementById('file-tools-audio')?.click()}
                            style={{ border: `2px dashed ${snippetForm.media_metadata?.audio?.url ? '#22c55e' : '#d1d5db'}`, borderRadius: '10px', padding: '24px', textAlign: 'center', cursor: 'pointer', background: snippetForm.media_metadata?.audio?.url ? '#f0fdf4' : '#f9fafb' }}>
                            <input id="file-tools-audio" type="file" accept="audio/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleMediaUpload(f, 'audio'); e.target.value = ''; }} />
                            {mediaUploading.audio ? <span style={{ fontSize: '13px', color: '#6b7280' }}>{language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}</span>
                              : snippetForm.media_metadata?.audio?.url ? (
                                <div><div style={{ fontSize: '28px', marginBottom: '6px' }}>🎵</div>
                                  <audio controls src={snippetForm.media_metadata.audio.url} style={{ width: '100%', marginTop: '8px' }} />
                                  <button type="button" onClick={e => { e.stopPropagation(); setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, audio: undefined } })); }} style={{ marginTop: '6px', fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>{language === 'ar' ? 'إزالة' : 'Remove'}</button>
                                </div>
                              ) : (
                                <div><FaMicrophone style={{ fontSize: '28px', color: '#9ca3af', marginBottom: '6px' }} />
                                  <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600 }}>{language === 'ar' ? 'اسحب ملفاً صوتياً أو انقر للرفع' : 'Drag audio or click to upload'}</div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>OGG · MP3 · M4A — max 16 MB</div>
                                </div>
                              )}
                          </div>
                          <input className="wa-input" placeholder={language === 'ar' ? 'أو الصق رابطاً صوتياً' : 'Or paste audio URL'} value={snippetForm.media_metadata?.audio?.url || ''} onChange={e => setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, audio: e.target.value ? { url: e.target.value } : undefined } }))} style={{ fontSize: '12px', marginTop: '8px', width: '100%' }} />
                        </div>
                      )}

                      {/* LOCATION */}
                      {mt === 'location' && (
                        <div>
                          <button type="button" onClick={() => setShowLocationPicker(true)} style={{ padding: '10px 18px', border: `2px dashed ${snippetForm.media_metadata?.location?.lat != null ? '#22c55e' : '#d1d5db'}`, borderRadius: '10px', background: snippetForm.media_metadata?.location?.lat != null ? '#f0fdf4' : '#f9fafb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>
                            <FaMapMarkerAlt style={{ color: snippetForm.media_metadata?.location?.lat != null ? '#22c55e' : '#ef4444' }} />
                            {snippetForm.media_metadata?.location?.lat != null ? (snippetForm.media_metadata.location.name || `${snippetForm.media_metadata.location.lat.toFixed(4)}, ${snippetForm.media_metadata.location.lng.toFixed(4)}`) : (language === 'ar' ? 'اختر موقعاً على الخريطة' : 'Pick location on map')}
                          </button>
                          {snippetForm.media_metadata?.location?.lat != null && (
                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                              <input className="wa-input" placeholder={language === 'ar' ? 'اسم الموقع (اختياري)' : 'Location name (optional)'} value={snippetForm.media_metadata.location.name || ''} onChange={e => setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, location: { ...f.media_metadata.location, name: e.target.value } } }))} style={{ flex: 1 }} />
                              <button type="button" onClick={() => setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, location: undefined } }))} style={{ fontSize: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>{language === 'ar' ? 'إزالة' : 'Remove'}</button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Live preview */}
                      {(() => {
                        const hasContent = snippetForm.content?.trim();
                        const hasMeta = snippetForm.media_metadata?.image?.url || snippetForm.media_metadata?.video?.url || snippetForm.media_metadata?.audio?.url || snippetForm.media_metadata?.location?.lat != null;
                        if (!hasContent && !hasMeta) return null;
                        const dir = detectTextDirection(snippetForm.content);
                        return (
                          <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{language === 'ar' ? 'معاينة' : 'Preview'}</label>
                            <div style={{ background: '#e5ddd5', borderRadius: '10px', padding: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                              <div style={{ maxWidth: '80%', background: '#dcf8c6', borderRadius: '12px 2px 12px 12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', fontFamily: whatsappFontFamily }}>
                                {mt === 'image' && snippetForm.media_metadata?.image?.url && <img src={snippetForm.media_metadata.image.url} alt="" style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />}
                                {mt === 'video' && snippetForm.media_metadata?.video?.url && <div style={{ background: '#000', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '24px' }}>▶</div>}
                                {mt === 'audio' && snippetForm.media_metadata?.audio?.url && <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', background: '#f0fdf4' }}><FaMicrophone style={{ color: '#25D366' }} /><div style={{ flex: 1, height: '3px', background: '#d1d5db', borderRadius: '2px' }} /><span style={{ fontSize: '11px', color: '#6b7280' }}>0:00</span></div>}
                                {mt === 'location' && snippetForm.media_metadata?.location?.lat != null && <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}><span style={{ fontSize: '18px' }}>📍</span><div><div style={{ fontSize: '12px', fontWeight: 700 }}>{snippetForm.media_metadata.location.name || (language === 'ar' ? 'الموقع' : 'Location')}</div><div style={{ fontSize: '11px', color: '#6b7280' }}>{snippetForm.media_metadata.location.lat.toFixed(4)}, {snippetForm.media_metadata.location.lng.toFixed(4)}</div></div></div>}
                                {hasContent && <div style={{ padding: '9px 13px', fontSize: '14px', color: '#111827', direction: dir, textAlign: dir === 'rtl' ? 'right' : 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.5' }}>{renderFormattedText(snippetForm.content)}</div>}
                                <div style={{ padding: '2px 13px 7px', fontSize: '11px', color: '#6b7280', textAlign: 'right' }}>{new Date().toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })} ✓✓</div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                {/* Save / Cancel */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" className="wa-btn wa-btn-primary" onClick={saveSnippet} disabled={snippetsSaving}>
                    {snippetsSaving ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : editingSnippetId ? (language === 'ar' ? '✓ تحديث' : '✓ Update') : (language === 'ar' ? '+ إضافة' : '+ Add')}
                  </button>
                  {editingSnippetId && (
                    <button type="button" className="wa-btn wa-btn-secondary" onClick={() => { setEditingSnippetId(null); setSnippetForm({ ...EMPTY_SNIPPET }); }} disabled={snippetsSaving}>
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                  )}
                </div>
              </div>

              {/* Location Picker Modal */}
              {showLocationPicker && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowLocationPicker(false)}>
                  <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', maxWidth: '600px', width: '100%' }} onClick={e => e.stopPropagation()}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0, fontSize: '15px' }}>{language === 'ar' ? 'اختر موقعاً' : 'Pick location'}</h3>
                      <button type="button" onClick={() => setShowLocationPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>×</button>
                    </div>
                    <div style={{ height: '340px' }}>
                      <MapContainer center={[snippetForm.media_metadata?.location?.lat ?? 24.7136, snippetForm.media_metadata?.location?.lng ?? 46.6753]} zoom={13} style={{ height: '100%', width: '100%' }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                        <LocationMarker position={snippetForm.media_metadata?.location ? [snippetForm.media_metadata.location.lat, snippetForm.media_metadata.location.lng] : null} setPosition={pos => { if (pos) setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, location: { lat: pos[0], lng: pos[1], name: f.media_metadata?.location?.name } } })); }} />
                      </MapContainer>
                    </div>
                    <div style={{ padding: '10px 12px', display: 'flex', gap: '8px' }}>
                      <input className="wa-input" placeholder={language === 'ar' ? 'اسم الموقع (اختياري)' : 'Location name (optional)'} value={snippetForm.media_metadata?.location?.name || ''} onChange={e => setSnippetForm(f => ({ ...f, media_metadata: { ...f.media_metadata, location: f.media_metadata?.location ? { ...f.media_metadata.location, name: e.target.value } : { lat: 24.7136, lng: 46.6753, name: e.target.value } } }))} style={{ flex: 1 }} />
                      <button type="button" className="wa-btn wa-btn-primary" onClick={() => setShowLocationPicker(false)}>{language === 'ar' ? 'تم' : 'Done'}</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Link Modal */}
              {showLinkModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={() => setShowLinkModal(false)}>
                  <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: '300px' }} onClick={e => e.stopPropagation()}>
                    <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>{language === 'ar' ? 'إدراج رابط' : 'Insert Link'}</h3>
                    <input className="wa-input" placeholder={language === 'ar' ? 'نص الرابط (اختياري)' : 'Display text (optional)'} value={linkModalText} onChange={e => setLinkModalText(e.target.value)} style={{ width: '100%', marginBottom: '10px' }} />
                    <input className="wa-input" placeholder="https://..." value={linkModalUrl} onChange={e => setLinkModalUrl(e.target.value)} style={{ width: '100%', marginBottom: '14px' }} />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button type="button" className="wa-btn wa-btn-secondary" onClick={() => setShowLinkModal(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                      <button type="button" className="wa-btn wa-btn-primary" onClick={applyLink} disabled={!linkModalUrl.trim()}>{language === 'ar' ? 'إدراج' : 'Insert'}</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Smart URL Modal */}
              {smartUrlModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSmartUrlModal(null)}>
                  <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', minWidth: '300px' }} onClick={e => e.stopPropagation()}>
                    <h3 style={{ margin: '0 0 10px', fontSize: '15px' }}>{language === 'ar' ? 'رابط وسائط مكتشف' : 'Media URL detected'}</h3>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px' }}>{language === 'ar' ? `إرفاق كـ ${smartUrlModal.type}؟` : `Attach as ${smartUrlModal.type}?`}</p>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '14px', wordBreak: 'break-all' }}>{smartUrlModal.url}</div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button type="button" className="wa-btn wa-btn-secondary" onClick={() => setSmartUrlModal(null)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                      <button type="button" className="wa-btn wa-btn-primary" onClick={applySmartUrl}>{language === 'ar' ? 'إرفاق' : 'Attach'}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
            </div>
          )}
        </div>
      )}

      {/* ════════════════ INTERACTIVE MESSAGES TAB ════════════════ */}
      {subTab === 'interactive' && (
        <div style={{ width: '100%' }}>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
                {language === 'ar' ? 'الرسائل التفاعلية' : 'Interactive messages'}
              </h3>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                {language === 'ar' ? 'عمودان: القوالب والنموذج' : 'Two columns: list & form'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.5, fontWeight: 500, maxWidth: '50ch' }}>
              {language === 'ar'
                ? 'تُرسل من الوارد خلال نافذة المحادثة مع العميل (٢٤ ساعة من آخر رسالته).'
                : 'Send from the inbox within the messaging session window (24h from the customer\'s last message).'}
            </p>
          </div>

          <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr' }}>
            {/* Presets list */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '18px' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', marginBottom: '14px' }}>
                {language === 'ar' ? 'القوالب المحفوظة' : 'Saved Presets'}
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b', fontWeight: 400 }}>({presets.length})</span>
              </div>
              {presetsLoading ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</div>
              ) : presets.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', color: '#64748b', fontSize: '13px' }}>
                  {language === 'ar' ? 'لا توجد قوالب. أضف واحداً.' : 'No presets yet. Add one.'}
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {presets.map(p => {
                    const pt = p.preset_type || 'button';
                    const listData = pt === 'list' ? parseStoredButtons(p.buttons, 'list') : null;
                    const btns = pt === 'button'
                      ? (Array.isArray(p.buttons) ? p.buttons : (typeof p.buttons === 'string' ? (() => { try { return JSON.parse(p.buttons); } catch { return []; } })() : []))
                      : [];
                    const listRows = pt === 'list' && listData
                      ? (listData.sections || []).flatMap((s) => (s.rows || []).filter((r) => (r.title || '').trim()))
                      : [];
                    return (
                      <div key={p.id} style={{ border: `1.5px solid ${editingPresetId === p.id ? '#6366f1' : '#e2e8f0'}`, borderRadius: '10px', padding: '12px', background: editingPresetId === p.id ? '#fafafe' : '#fff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>{p.label}</span>
                              {pt === 'list' && (
                                <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: '#fef3c7', color: '#92400e' }}>{language === 'ar' ? 'قائمة' : 'List'}</span>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569', marginBottom: '8px', lineHeight: '1.4' }}>{p.body}</div>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              {pt === 'button' ? btns.map((b, i) => (
                                <span key={i} style={{ padding: '3px 10px', border: '1.5px solid #6366f1', borderRadius: '20px', fontSize: '11px', color: '#4f46e5', fontWeight: 600 }}>{b.title}</span>
                              )) : (
                                <>
                                  {listData?.listButton ? (
                                    <span style={{ padding: '3px 10px', border: '1.5px solid #d97706', borderRadius: '20px', fontSize: '11px', color: '#b45309', fontWeight: 600 }}>▾ {listData.listButton}</span>
                                  ) : null}
                                  {listRows.map((r, i) => (
                                    <span key={i} style={{ padding: '3px 10px', border: '1.5px solid #94a3b8', borderRadius: '6px', fontSize: '11px', color: '#475569', fontWeight: 500 }}>{r.title}</span>
                                  ))}
                                </>
                              )}
                            </div>
                            {p.usage_count > 0 && <div style={{ marginTop: '6px', fontSize: '11px', color: '#64748b' }}>{language === 'ar' ? `استُخدم ${p.usage_count}×` : `Used ${p.usage_count}×`}</div>}
                          </div>
                          {canManage && (
                            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                              <button onClick={() => { setEditingPresetId(p.id); setPresetForm(presetRecordToForm(p)); }}
                                style={{ padding: '4px 8px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', color: '#475569' }}>
                                <FaEdit />
                              </button>
                              <button onClick={() => clonePreset(p)} disabled={presetsSaving} style={{ padding: '4px 8px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', color: '#166534' }}>
                                <FaCopy />
                              </button>
                              <button onClick={async () => { if (!window.confirm(language === 'ar' ? 'حذف هذا القالب؟' : 'Delete this preset?')) return; try { setPresetsSaving(true); await deleteWhatsAppInteractivePreset(p.id); toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted'); await loadPresets(); } catch { toast.error(language === 'ar' ? 'فشل الحذف' : 'Delete failed'); } finally { setPresetsSaving(false); } }}
                                disabled={presetsSaving} style={{ padding: '4px 8px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', color: '#991b1b' }}>
                                <FaTrash />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Preset form + preview — visible only to users with wa_tools_manage */}
            {canManage && (
              <div style={{ display: 'grid', gap: '16px', alignContent: 'start' }}>
                {/* Form */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '18px' }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#334155', marginBottom: '14px' }}>
                    {editingPresetId ? (language === 'ar' ? '✎ تعديل القالب' : '✎ Edit Preset') : (language === 'ar' ? '+ قالب جديد' : '+ New Preset')}
                  </div>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase' }}>{language === 'ar' ? 'الاسم الداخلي *' : 'Internal Label *'}</label>
                      <input className="wa-input" placeholder={language === 'ar' ? 'مثال: استفسار موعد المعاينة' : 'e.g. Viewing interest check'} value={presetForm.label} onChange={e => setPresetForm(f => ({ ...f, label: e.target.value }))} style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase' }}>
                        {language === 'ar' ? 'نص الرسالة *' : 'Message Body *'}
                        <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 400, color: '#9ca3af' }}>{(presetForm.body || '').length} / 1024</span>
                      </label>
                      <textarea className="wa-input" placeholder={language === 'ar' ? 'هل أنت مهتم بمشاهدة العقار؟' : 'Are you interested in viewing this property?'} value={presetForm.body} onChange={e => setPresetForm(f => ({ ...f, body: e.target.value.slice(0, 1024) }))} style={{ width: '100%', minHeight: '80px', resize: 'vertical', fontFamily: 'inherit', direction: detectTextDirection(presetForm.body) }} rows={3} spellCheck />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>
                        {language === 'ar' ? 'النوع' : 'Type'}
                      </label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button type="button" onClick={() => setPresetForm(f => ({ ...f, preset_type: 'button' }))} style={{ padding: '8px 14px', borderRadius: '8px', border: presetForm.preset_type !== 'list' ? '2px solid #6366f1' : '1px solid #e2e8f0', background: presetForm.preset_type !== 'list' ? '#eef2ff' : '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#4338ca' }}>
                          {language === 'ar' ? 'أزرار (1–3)' : 'Buttons (1–3)'}
                        </button>
                        <button type="button" onClick={() => setPresetForm(f => ({ ...f, preset_type: 'list' }))} style={{ padding: '8px 14px', borderRadius: '8px', border: presetForm.preset_type === 'list' ? '2px solid #d97706' : '1px solid #e2e8f0', background: presetForm.preset_type === 'list' ? '#fffbeb' : '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#b45309' }}>
                          {language === 'ar' ? 'قائمة (حتى 10)' : 'List (up to 10)'}
                        </button>
                      </div>
                      <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#94a3b8', lineHeight: 1.45, fontWeight: 500 }}>
                        {presetForm.preset_type !== 'list'
                          ? (language === 'ar'
                            ? 'حد المنصة: ثلاثة أزرار كحدّ أقصى.'
                            : 'Platform limit: 3 buttons max.')
                          : (language === 'ar'
                            ? 'حد المنصة: عشرة صفوف كحدّ أقصى في القائمة.'
                            : 'Platform limit: 10 rows max in the list.')}
                      </p>
                    </div>
                    {presetForm.preset_type !== 'list' ? (
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>
                        {language === 'ar' ? 'الأزرار (1–3)' : 'Buttons (1–3)'}
                      </label>
                      <div style={{ display: 'grid', gap: '7px' }}>
                        {presetForm.buttons.map((btn, i) => (
                          <div key={i} style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '8px' }}>
                            <div style={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
                              <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 700, width: '20px', flexShrink: 0 }}>{i + 1}.</span>
                              <input className="wa-input" placeholder={language === 'ar' ? `الزر ${i + 1} (حتى 20 حرفاً)` : `Button ${i + 1} (max 20 chars)`} value={btn.title} onChange={e => updateButton(i, 'title', e.target.value.slice(0, 20))} style={{ flex: 1, fontSize: '13px' }} maxLength={20} />
                              <span style={{ fontSize: '10px', color: '#94a3b8', width: '28px', textAlign: 'right', flexShrink: 0 }}>{btn.title.length}/20</span>
                              {presetForm.buttons.length > 1 && (
                                <button type="button" onClick={() => removeButton(i)} style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '13px' }}><FaTimes /></button>
                              )}
                            </div>
                            <InteractivePresetRuleEditor
                              language={language}
                              rule={btn.rule}
                              onTagsChange={(v) => updateButtonRule(i, 'add_tags', v)}
                              onPriorityChange={(v) => updateButtonRule(i, 'priority', v)}
                              onActivityChange={(v) => updateButtonRule(i, 'suggest_activity', v)}
                              suggestedTags={orgTagSuggestions}
                            />
                          </div>
                        ))}
                        {presetForm.buttons.length < 3 && (
                          <button type="button" onClick={addButton} style={{ padding: '7px', border: '1.5px dashed #d1d5db', borderRadius: '7px', background: '#f9fafb', cursor: 'pointer', fontSize: '12px', color: '#6366f1', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                            <FaPlus style={{ fontSize: '10px' }} /> {language === 'ar' ? 'إضافة زر' : 'Add button'}
                          </button>
                        )}
                      </div>
                    </div>
                    ) : (
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase' }}>
                        {language === 'ar' ? 'نص زر فتح القائمة *' : 'List menu button *'}
                        <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 400, color: '#9ca3af' }}>{(presetForm.listButton || '').length}/{LIST_BUTTON_MAX}</span>
                      </label>
                      <input className="wa-input" placeholder={language === 'ar' ? 'مثال: اختر الموعد' : 'e.g. Pick a time'} value={presetForm.listButton} onChange={e => setPresetForm(f => ({ ...f, listButton: e.target.value.slice(0, LIST_BUTTON_MAX) }))} style={{ width: '100%', marginBottom: '12px' }} maxLength={LIST_BUTTON_MAX} />
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
                        {language === 'ar' ? `الصفوف ذات العنوان: ${listRowTotal()} / ${MAX_LIST_ROWS}` : `Rows with title: ${listRowTotal()} / ${MAX_LIST_ROWS}`}
                      </div>
                      {presetForm.sections.map((sec, si) => (
                        <div key={si} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', marginBottom: '10px', background: '#fafafa' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                            <input className="wa-input" placeholder={language === 'ar' ? 'عنوان القسم (اختياري)' : 'Section title (optional)'} value={sec.title} onChange={e => updateSectionTitle(si, e.target.value.slice(0, 24))} style={{ flex: 1, fontSize: '12px' }} maxLength={24} />
                            {presetForm.sections.length > 1 && (
                              <button type="button" onClick={() => removeListSection(si)} style={{ padding: '4px 8px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#991b1b' }}>{language === 'ar' ? 'حذف القسم' : 'Remove section'}</button>
                            )}
                          </div>
                          <div style={{ display: 'grid', gap: '8px' }}>
                            {sec.rows.map((row, ri) => (
                              <div key={row.id || ri} style={{ display: 'grid', gap: '6px', padding: '8px', background: '#fff', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                  <input className="wa-input" placeholder={language === 'ar' ? 'عنوان الخيار (24)' : 'Row title (24)'} value={row.title} onChange={e => updateListRow(si, ri, 'title', e.target.value.slice(0, 24))} style={{ flex: 1, fontSize: '12px' }} maxLength={24} />
                                  {sec.rows.length > 1 && (
                                    <button type="button" onClick={() => removeListRow(si, ri)} style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><FaTimes /></button>
                                  )}
                                </div>
                                <input className="wa-input" placeholder={language === 'ar' ? 'وصف اختياري (72)' : 'Optional description (72)'} value={row.description} onChange={e => updateListRow(si, ri, 'description', e.target.value.slice(0, 72))} style={{ fontSize: '11px' }} maxLength={72} />
                                <InteractivePresetRuleEditor
                                  language={language}
                                  rule={row.rule}
                                  onTagsChange={(v) => updateListRowRule(si, ri, 'add_tags', v)}
                                  onPriorityChange={(v) => updateListRowRule(si, ri, 'priority', v)}
                                  onActivityChange={(v) => updateListRowRule(si, ri, 'suggest_activity', v)}
                                  suggestedTags={orgTagSuggestions}
                                />
                              </div>
                            ))}
                            <button type="button" onClick={() => addListRow(si)} disabled={listRowTotal() >= MAX_LIST_ROWS} style={{ padding: '6px', border: '1.5px dashed #d1d5db', borderRadius: '7px', background: listRowTotal() >= MAX_LIST_ROWS ? '#f3f4f6' : '#fff', cursor: listRowTotal() >= MAX_LIST_ROWS ? 'not-allowed' : 'pointer', fontSize: '11px', color: '#b45309', fontWeight: 600 }}>
                              <FaPlus style={{ fontSize: '9px' }} /> {language === 'ar' ? 'صف في هذا القسم' : 'Add row in section'}
                            </button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={addListSection} style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                        {language === 'ar' ? '+ قسم جديد' : '+ Add section'}
                      </button>
                    </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" className="wa-btn wa-btn-primary" onClick={savePreset} disabled={presetsSaving}>
                        {presetsSaving ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : editingPresetId ? (language === 'ar' ? '✓ تحديث' : '✓ Update') : (language === 'ar' ? '+ حفظ' : '+ Save')}
                      </button>
                      {editingPresetId && (
                        <button type="button" className="wa-btn wa-btn-secondary" onClick={() => { setEditingPresetId(null); setPresetForm({ ...EMPTY_INTERACTIVE_PRESET_FORM }); }} disabled={presetsSaving}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Live preview */}
                {(presetForm.body.trim() || (presetForm.preset_type === 'list' ? (presetForm.listButton || '').trim() || countListRows(presetForm.sections) : presetForm.buttons.some(b => b.title))) && (
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>{language === 'ar' ? 'معاينة' : 'Preview'}</div>
                    <div style={{ background: '#e5ddd5', borderRadius: '10px', padding: '10px' }}>
                      <div style={{ maxWidth: '85%', background: '#fff', borderRadius: '2px 12px 12px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', overflow: 'hidden', fontFamily: whatsappFontFamily }}>
                        {presetForm.body.trim() && (
                          <div style={{ padding: '10px 13px', fontSize: '14px', color: '#111827', direction: detectTextDirection(presetForm.body), textAlign: detectTextDirection(presetForm.body) === 'rtl' ? 'right' : 'left', lineHeight: '1.5' }}>
                            {presetForm.body}
                          </div>
                        )}
                        <div style={{ padding: '2px 13px 8px', fontSize: '11px', color: '#6b7280', textAlign: 'right' }}>
                          {new Date().toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {presetForm.preset_type === 'list' ? (
                          <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 13px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#b45309' }}>
                            ▾ {presetForm.listButton || (language === 'ar' ? 'القائمة' : 'View list')}
                          </div>
                        ) : (
                        <div style={{ borderTop: '1px solid #e5e7eb' }}>
                          {presetForm.buttons.filter(b => b.title.trim()).map((btn, i) => (
                            <div key={i} style={{ padding: '10px 13px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#0e8cf1', borderTop: i > 0 ? '1px solid #e5e7eb' : 'none', cursor: 'default' }}>
                              {btn.title}
                            </div>
                          ))}
                        </div>
                        )}
                        {presetForm.preset_type === 'list' && (
                          <div style={{ padding: '0 12px 12px', fontSize: '11px', color: '#64748b' }}>
                            {presetForm.sections.map((s, si) => (
                              <div key={si} style={{ marginTop: '8px' }}>
                                {s.title?.trim() ? <div style={{ fontWeight: 700, marginBottom: '4px', color: '#334155' }}>{s.title}</div> : null}
                                {s.rows.filter(r => r.title.trim()).map((r, ri) => (
                                  <div key={ri} style={{ padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                                    <span style={{ fontWeight: 600, color: '#111' }}>{r.title}</span>
                                    {r.description?.trim() ? <div style={{ fontSize: '10px', color: '#94a3b8' }}>{r.description}</div> : null}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                      {language === 'ar' ? '↑ كيف تظهر الرسالة على هاتف العميل' : '↑ How it appears on the client\'s phone'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTools;
